import type { FilterCelice, Porocilo, VrsticaPorocila } from '@koda/shared';
import { useEffect, useState, type JSX } from 'react';

import { jeNegativen, odstotek, znesek } from '../lib/format';
import { useBesedila } from '../lib/jezik';
import type { Celica } from './PanelRazclenitve';

interface Lastnosti {
  porocilo: Porocilo;
  /**
   * Do katere ravni je mogoče razširiti vrstice. Privzeto sta to skupina in
   * prva raven členitve; podrobni konti se odprejo s stikalom.
   */
  globina: number;
  /** Klik na številko odpre razčlenitev te celice. */
  naCelico: (celica: Celica) => void;
  /** Vrstica odprte razčlenitve; v tabeli je označena. */
  izbrana?: VrsticaPorocila | null;
}

interface Vidna {
  vrstica: VrsticaPorocila;
  raven: number;
}

/** Vrstice, razširjene glede na odprte ključe in dovoljeno globino. */
function vidneVrstice(
  vrstice: VrsticaPorocila[],
  odprte: Set<string>,
  globina: number,
  raven = 0,
): Vidna[] {
  return vrstice.flatMap((v) => [
    { vrstica: v, raven },
    ...(odprte.has(v.kljuc) && razsirljiva(v, raven, globina)
      ? vidneVrstice(v.otroci, odprte, globina, raven + 1)
      : []),
  ]);
}

/**
 * Ali se vrstica lahko razsiri: do dovoljene globine; vrstica, ki je
 * privzeto odprta (konto z denarnimi sredstvi), se razsiri tudi cez njo,
 * da je povezava z denarnim tokom vidna brez stikala za podrobne konte.
 */
function razsirljiva(v: VrsticaPorocila, raven: number, globina: number): boolean {
  return v.otroci.length > 0 && (raven + 1 < globina || v.privzetoOdprta === true);
}

/**
 * Tabela poročila.
 *
 * Klik na naziv razširi vrstico na naslednjo raven, klik na številko pa odpre
 * razčlenitev te celice do knjižb (docs/ARHITEKTURA.md, 6.2).
 */
/**
 * Privzeto odprte vrstice: prva raven členitve je vidna takoj.
 *
 * To je primarni pogled — stranka naj vidi 760, 762, 768 brez klikanja,
 * globlje ravni pa ostanejo zložene.
 */
/** Nazivi nadrejenih vrstic do `cilj`; prazno, kadar je vrstica na vrhu. */
function potDo(vrstice: VrsticaPorocila[], cilj: VrsticaPorocila): string[] | null {
  for (const v of vrstice) {
    if (v === cilj) return [];
    const spodaj = potDo(v.otroci, cilj);
    if (spodaj !== null) return [v.naziv, ...spodaj];
  }
  return null;
}

function privzetoOdprte(vrstice: VrsticaPorocila[]): Set<string> {
  const odprte = new Set(vrstice.map((v) => v.kljuc));
  const dodaj = (seznam: VrsticaPorocila[]): void => {
    for (const v of seznam) {
      if (v.privzetoOdprta === true) odprte.add(v.kljuc);
      dodaj(v.otroci);
    }
  };
  dodaj(vrstice);
  return odprte;
}

export function TabelaPorocila({
  porocilo,
  globina,
  naCelico: odpriCelico,
  izbrana = null,
}: Lastnosti): JSX.Element {
  const b = useBesedila();
  /** Razčlenitev dobi še pot do vrstice, da glava panela pove, kaj je odprto. */
  const naCelico = (celica: Celica): void => {
    odpriCelico({ ...celica, pot: potDo(porocilo.vrstice, celica.vrstica) ?? [] });
  };
  const razredIzbrane = (vrstica: VrsticaPorocila): string => (vrstica === izbrana ? ' izbrana' : '');
  const [odprte, setOdprte] = useState<Set<string>>(() => privzetoOdprte(porocilo.vrstice));

  /** Celica enega meseca; pri stanjih je znesek kumulativa od otvoritve. */
  const celicaObdobja = (vrstica: VrsticaPorocila, i: number): Celica => {
    const stolpec = porocilo.stolpci[i]!;
    return {
      vrstica,
      obdobjeOd: porocilo.kumulativno ? 0 : stolpec.obdobje,
      // Začetno stanje pri razponu zajame otvoritev in mesece pred razponom.
      obdobjeDo: stolpec.doObdobja ?? stolpec.obdobje,
      naslovObdobja: porocilo.kumulativno
        ? `${stolpec.naziv} (stanje na koncu meseca)`
        : stolpec.naziv,
      znesek: vrstica.zneski[i] ?? '0',
    };
  };

  /**
   * Celica stolpca, ki nosi svoj filter (primerjava T/T-1).
   *
   * Stolpca let imata vsak svoj filter, stolpec razlike pa oba — takrat
   * razčlenitev pokaže knjižbe vsakega leta posebej (docs/ARHITEKTURA.md, 6.2).
   * Naslov in znesek dela se vzameta iz stolpca, katerega filter je enak.
   */
  const celicaSFiltri = (vrstica: VrsticaPorocila, i: number, filtri: FilterCelice[]): Celica => {
    const stolpec = porocilo.stolpci[i]!;
    return {
      vrstica,
      obdobjeOd: stolpec.obdobje,
      obdobjeDo: stolpec.obdobje,
      naslovObdobja: stolpec.naziv,
      znesek: vrstica.zneski[i] ?? '0',
      deli: filtri.map((f) => {
        const j = (vrstica.filtriStolpcev ?? []).findIndex(
          (fs) => fs.length === 1 && fs[0]!.leto === f.leto,
        );
        return {
          naslov: porocilo.stolpci[j]?.naziv ?? String(f.leto),
          filter: f,
          znesek: vrstica.zneski[j] ?? '0',
        };
      }),
    };
  };

  /**
   * Celica stolpca „skupaj": zajame vsa obdobja poročila.
   *
   * Brez tega letne vsote ni mogoče razčleniti — nekdo bo hotel videti
   * celoletne knjižbe posameznega stroška, ne le enega meseca.
   */
  const celicaSkupaj = (vrstica: VrsticaPorocila): Celica => {
    const prvi = porocilo.stolpci[0];
    const zadnji = porocilo.stolpci[porocilo.stolpci.length - 1];
    return {
      vrstica,
      obdobjeOd: vrstica.filter.obdobjeOd,
      obdobjeDo: vrstica.filter.obdobjeDo,
      naslovObdobja:
        prvi === undefined || zadnji === undefined
          ? b.vsaObdobja
          : `${prvi.naziv} – ${zadnji.naziv}`,
      znesek: vrstica.skupaj,
    };
  };

  // Ob prehodu na drugo poročilo so ključi drugi; začnemo spet odprto.
  useEffect(() => {
    setOdprte(privzetoOdprte(porocilo.vrstice));
  }, [porocilo]);

  const preklopi = (kljuc: string): void => {
    setOdprte((prej) => {
      const novo = new Set(prej);
      if (novo.has(kljuc)) novo.delete(kljuc);
      else novo.add(kljuc);
      return novo;
    });
  };

  /**
   * Ena številka v stolpcu.
   *
   * Odstotek ni vsota knjižb: zapiše se kot odstotek in se ne odpre v
   * razčlenitev — pod njim ni česa pokazati. Pove ga `filtriStolpcev` s
   * praznim seznamom filtrov.
   */
  const celicaStolpca = (vrstica: VrsticaPorocila, i: number, klikljiva: boolean): JSX.Element => {
    const stolpec = porocilo.stolpci[i]!;
    const z = vrstica.zneski[i] ?? '';
    const besedilo = stolpec.enota === 'odstotek' ? odstotek(z) : znesek(z);
    const filtri = vrstica.filtriStolpcev?.[i];
    const odpri = klikljiva && (filtri === undefined || filtri.length > 0);
    return (
      <td key={stolpec.obdobje} className="stevilka">
        {odpri ? (
          <button
            type="button"
            className={jeNegativen(z) ? 'znesek negativen' : 'znesek'}
            onClick={() => {
              naCelico(filtri === undefined ? celicaObdobja(vrstica, i) : celicaSFiltri(vrstica, i, filtri));
            }}
            title={b.namigCelice}
          >
            {besedilo}
          </button>
        ) : (
          <span className={jeNegativen(z) ? 'negativen' : undefined}>{besedilo}</span>
        )}
      </td>
    );
  };

  const vidne = vidneVrstice(porocilo.vrstice, odprte, globina);

  return (
    <div className="tabela-ovoj">
      <table className="porocilo">
        <thead>
          <tr>
            <th className="naziv">{b.postavka}</th>
            {porocilo.stolpci.map((s) => (
              <th key={s.obdobje} className="stevilka">
                {s.naziv}
              </th>
            ))}
            {porocilo.prikaziSkupaj && (
              <th className="stevilka skupaj">{porocilo.nazivSkupaj ?? b.skupaj}</th>
            )}
          </tr>
        </thead>
        <tbody>
          {vidne.map(({ vrstica, raven }) => (
            <tr
              key={vrstica.kljuc}
              className={`raven-${String(raven)}${vrstica.vrstaVrstice === 'vmesna' ? ' vmesna' : ''}${razredIzbrane(vrstica)}`}
            >
              <th
                className="naziv"
                style={{ paddingLeft: `${String(raven * 1.25 + 0.5)}rem` }}
                title={vrstica.opomba ?? undefined}
              >
                {razsirljiva(vrstica, raven, globina) ? (
                  <button
                    type="button"
                    className="razsiri"
                    onClick={() => {
                      preklopi(vrstica.kljuc);
                    }}
                    aria-expanded={odprte.has(vrstica.kljuc)}
                  >
                    <span aria-hidden="true">{odprte.has(vrstica.kljuc) ? '−' : '+'}</span>
                    {vrstica.sifra !== null && <code>{vrstica.sifra}</code>}
                    {vrstica.naziv}
                  </button>
                ) : (
                  <span className="brez-otrok">
                    {vrstica.sifra !== null && <code>{vrstica.sifra}</code>}
                    {vrstica.naziv}
                    {vrstica.opomba !== null && (
                      <abbr className="opomba" title={vrstica.opomba} aria-label={vrstica.opomba}>
                        *
                      </abbr>
                    )}
                  </span>
                )}
              </th>
              {vrstica.zneski.map((_, i) => celicaStolpca(vrstica, i, true))}
              {porocilo.prikaziSkupaj && (
                <td className="stevilka skupaj">
                  <button
                    type="button"
                    className={jeNegativen(vrstica.skupaj) ? 'znesek negativen' : 'znesek'}
                    onClick={() => {
                      naCelico(celicaSkupaj(vrstica));
                    }}
                    title={b.namigCelicaSkupaj}
                  >
                    {znesek(vrstica.skupaj)}
                  </button>
                </td>
              )}
            </tr>
          ))}
          {porocilo.skupaj !== null && (
            <tr className={`vsota${razredIzbrane(porocilo.skupaj)}`}>
              <th className="naziv">{porocilo.skupaj.naziv}</th>
              {porocilo.skupaj.zneski.map((_, i) => celicaStolpca(porocilo.skupaj!, i, true))}
              {porocilo.prikaziSkupaj && (
                <td className="stevilka skupaj">
                  <button
                    type="button"
                    className={jeNegativen(porocilo.skupaj.skupaj) ? 'znesek negativen' : 'znesek'}
                    onClick={() => {
                      naCelico(celicaSkupaj(porocilo.skupaj!));
                    }}
                    title={b.namigCelicaSkupaj}
                  >
                    {znesek(porocilo.skupaj.skupaj)}
                  </button>
                </td>
              )}
            </tr>
          )}
          {porocilo.dodatneVrstice.map((v) => (
            <tr key={v.kljuc} className={`dodatna${razredIzbrane(v)}`}>
              <th className="naziv">{v.naziv}</th>
              {v.zneski.map((_, i) => celicaStolpca(v, i, v.klikljiva))}
              {porocilo.prikaziSkupaj && (
                <td className="stevilka skupaj">
                  <span className={jeNegativen(v.skupaj) ? 'negativen' : undefined}>
                    {v.skupaj === '' ? '' : znesek(v.skupaj)}
                  </span>
                </td>
              )}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
